import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ScopeBanner — visible at the top of every authenticated page.
 *
 * Behaviour:
 *   - admin: shows "Acesso total" pill (no toggle, admin always sees all)
 *   - procurement_manager: shows scope + "ver tudo" toggle (state from user.view_all_warehouses_enabled)
 *   - other roles: shows the list of warehouses in scope (read-only)
 *
 * Compact pill in the top bar; does not affect layout.
 */
export default function ScopeBanner() {
  const { user, login } = useContext(AuthContext);
  const [toggling, setToggling] = useState(false);
  if (!user) return null;

  const role = user.role;
  const effective = user.effective_warehouses || [];
  const isAdmin = role === 'admin';
  const isPM = role === 'procurement_manager';
  const viewAll = !!user.view_all_warehouses_enabled;

  const handleToggle = async () => {
    setToggling(true);
    try {
      const newVal = !viewAll;
      await axios.post(`${API}/users/me/view-all-warehouses?enabled=${newVal}`);
      // Refresh /me
      const r = await axios.get(`${API}/auth/me`);
      const token = localStorage.getItem('token');
      login(token, r.data);
      toast.success(newVal ? 'A ver todos os armazéns' : `A ver apenas o âmbito (${(r.data.allowed_warehouses || []).length})`);
    } catch (e) {
      toast.error('Erro ao alternar âmbito');
    } finally { setToggling(false); }
  };

  if (isAdmin) {
    return (
      <span
        data-testid="scope-banner-admin"
        className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200"
        title="Admin: acesso total a todos os armazéns"
      >
        <ShieldCheck className="h-3 w-3" /> Acesso total
      </span>
    );
  }

  if (isPM) {
    return (
      <button
        onClick={handleToggle}
        disabled={toggling}
        data-testid="scope-banner-pm-toggle"
        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border transition-colors ${
          viewAll
            ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
        }`}
        title={viewAll ? 'A ver TODOS os armazéns — click para limitar ao teu âmbito' : `Âmbito: ${(user.allowed_warehouses || []).join(', ') || 'nenhum'} — click para ver tudo`}
      >
        {viewAll ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        {viewAll ? 'A ver tudo' : `Âmbito (${(user.allowed_warehouses || []).length})`}
      </button>
    );
  }

  // warehouse_operator / technician / store — read-only scope display
  const count = effective.length;
  return (
    <span
      data-testid="scope-banner-restricted"
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
      title={count === 0
        ? 'Sem armazéns configurados — pede ao admin para te dar âmbito'
        : `Vês apenas: ${effective.join(', ')}`}
    >
      <EyeOff className="h-3 w-3" />
      {count === 0 ? 'Sem âmbito' : `${count} armazém${count > 1 ? 's' : ''}`}
    </span>
  );
}
